import { StatusCode } from 'hono/utils/http-status';
import { ErrorCode } from './error';

/**
 * Base error class for all application errors.
 * All custom errors should extend this class.
 */
export class AppError extends Error {
  constructor(
    public code: ErrorCode,
    message: string,
    public status: StatusCode = 400,
    public details?: unknown
  ) {
    super(message);
    this.name = this.constructor.name;
  }
}

/**
 * Validation and Input Errors
 * These errors occur when user input fails validation or is invalid
 */
export class ValidationError extends AppError {
  constructor(message: string, details?: unknown) {
    super(ErrorCode.VALIDATION_ERROR, message, 400, details);
  }
}

/**
 * Resource Errors
 * These errors occur when a requested resource cannot be found
 */
export class NotFoundError extends AppError {
  constructor(message: string) {
    super(ErrorCode.NOT_FOUND, message, 404);
  }
}

/**
 * Authentication and Authorization Errors
 * These errors occur when a user is not authenticated or lacks required permissions
 */
export class UnauthorizedError extends AppError {
  constructor(message: string = 'Unauthorized') {
    super(ErrorCode.UNAUTHORIZED, message, 401);
  }
}

export class PermissionDeniedError extends AppError {
  constructor(message: string = 'Permission denied') {
    super(ErrorCode.PERMISSION_DENIED, message, 403);
  }
}

/**
 * Packet Operation Errors
 * These errors occur during packet-related operations
 */
export class PacketError extends AppError {
  constructor(code: ErrorCode, message: string) {
    // Ensure the error code is packet-related or a valid status code
    const validCodes = [
      ErrorCode.PACKET_ERROR,
      ErrorCode.INVALID_STATUS_TRANSITION,
      ErrorCode.INVALID_ROLE_ASSIGNMENT,
      ErrorCode.NOT_FOUND,
      ErrorCode.PERMISSION_DENIED
    ];
    if (!validCodes.includes(code)) {
      throw new Error(`Invalid packet error code: ${code}`);
    }
    // Map status code based on error code
    let status: StatusCode = 400;
    if (code === ErrorCode.NOT_FOUND) {
      status = 404;
    } else if (code === ErrorCode.PERMISSION_DENIED) {
      status = 403;
    }
    super(code, message, status);
  }
}

export class InvalidStatusTransitionError extends PacketError {
  constructor(message: string) {
    super(ErrorCode.INVALID_STATUS_TRANSITION, message);
  }
}

export class InvalidRoleAssignmentError extends PacketError {
  constructor(message: string) {
    super(ErrorCode.INVALID_ROLE_ASSIGNMENT, message);
  }
}

/**
 * External Service Errors
 * These errors occur when external services fail
 */
export class EmailError extends AppError {
  constructor(message: string = 'Failed to send email', details?: { provider?: string, originalCode?: string }) {
    super(ErrorCode.EMAIL_SEND_FAILED, message, 503, details);
  }
}

/**
 * API Errors
 * These errors occur during API operations
 */
export class APIError extends AppError {
  constructor(message: string, status: number, data?: unknown) {
    super(ErrorCode.HTTP_ERROR, message, status as StatusCode, data);
  }
}

/**
 * Error thrown when an invitation token is invalid or not found
 */
export class InvalidInvitationTokenError extends AppError {
  constructor(message = 'Invalid or expired invitation token', details?: unknown) {
    super(ErrorCode.NOT_FOUND, message, 404, details);
  }
}

/**
 * Error thrown when an invitation has expired
 */
export class InvitationExpiredError extends AppError {
  constructor(message = 'Invitation has expired', details?: unknown) {
    super(ErrorCode.GONE, message, 410, details);
  }
}

/**
 * Error thrown when an invitation has already been accepted
 */
export class InvitationAlreadyAcceptedError extends AppError {
  constructor(message = 'Invitation has already been accepted', details?: unknown) {
    super(ErrorCode.CONFLICT, message, 409, details);
  }
}

/**
 * Error thrown when a user already has a role in a packet
 */
export class UserAlreadyHasRoleError extends AppError {
  constructor(message = 'User already has a role in this packet', details?: unknown) {
    super(ErrorCode.CONFLICT, message, 409, details);
  }
}

/**
 * Error thrown when trying to create an endorsement for a user who already has one for the packet
 */
export class DuplicateEndorsementError extends AppError {
  constructor(message = 'User already has an endorsement for this packet', details?: unknown) {
    super(ErrorCode.CONFLICT, message, 409, details);
  }
}

/**
 * Error thrown when trying to update or delete an endorsement the user doesn't own
 */
export class EndorsementOwnershipError extends AppError {
  constructor(message = 'You can only modify your own endorsements', details?: unknown) {
    super(ErrorCode.PERMISSION_DENIED, message, 403, details);
  }
}

/**
 * Error thrown when an endorsement is not found
 */
export class EndorsementNotFoundError extends NotFoundError {
  constructor(message = 'Endorsement not found', details?: unknown) {
    super(message);
    this.details = details;
  }
}

/**
 * Error thrown when trying to create an assessment for a user who already has one for the packet type
 */
export class DuplicateAssessmentError extends AppError {
  constructor(message = 'User already has an assessment for this packet type', details?: unknown) {
    super(ErrorCode.CONFLICT, message, 409, details);
  }
} 